/**
 * Document Type Registry
 *
 * Central registry containing all document type configuration.
 * This is the single source of truth for document type behavior across the application.
 *
 * NOTE: This file defines the canonical ApiDocumentType. The utils/documentTypeMapping.ts
 * re-exports from here for backward compatibility.
 */

/**
 * Valid API document types - the canonical source of truth
 * These match what each ParsedDocument class returns from get_document_type_name():
 * - ParsedOrder.get_document_type_name() → "order"
 * - ParsedOrderConfirmation.get_document_type_name() → "order_confirmation"
 * - ParsedIncomingQuote.get_document_type_name() → "incoming_quote"
 * - ParsedBackorderReport.get_document_type_name() → "backorder_report"
 * - ParsedProductDescription.get_document_type_name() → "product_description"
 */
export const API_DOCUMENT_TYPES = [
  'order',
  'order_confirmation',
  'incoming_quote',
  'backorder_report',
  'product_description',
] as const;

export type ApiDocumentType = typeof API_DOCUMENT_TYPES[number];

/**
 * Integration type options for document integrations
 */
export type IntegrationType = 'business_central' | 'sap_idoc_http' | 'sap_idoc_email' | 'email' | 'rest_api';

/** Legacy integration type alias kept for backward compatibility with existing MongoDB configs */
export const LEGACY_INTEGRATION_TYPE_MAP: Record<string, IntegrationType> = {
  sap_idoc: 'sap_idoc_http',
};

/** Normalize an integration type string, mapping legacy values to current ones */
export function normalizeIntegrationType(raw: string): IntegrationType {
  return (LEGACY_INTEGRATION_TYPE_MAP[raw] as IntegrationType) || raw as IntegrationType;
}

/**
 * Configuration for a single document type
 */
export interface DocumentTypeConfig {
  /** The API document type identifier */
  type: ApiDocumentType;
  /** The backend `ProcessType` id the API routes take where they are
   *  type-agnostic (dry run, chat submission) — `order` is `sales_order`
   *  server-side; every other slug is its own process type. */
  processType: string;

  /** Display configuration */
  display: {
    /** Singular label (e.g., "Order") */
    label: string;
    /** i18n key suffix under `dryRun.items` for what this type counts —
     *  order lines, product-description articles. */
    caseItems: 'lines' | 'articles';
    /** Plural label (e.g., "Orders") */
    labelPlural: string;
    /** Placeholder for search inputs */
    searchPlaceholder: string;
    /** Info tooltip for add tenant view */
    infoTooltip: string;
  };

  /** Route configuration */
  route: {
    /** Base path segment (e.g., "orders") */
    basePath: string;
    /** Generate detail page path */
    detailPath: (tenantId: string, documentId: string) => string;
  };

  /** Scheduler configuration */
  scheduler: {
    /** Scheduler key (e.g., "order_processing") */
    key: string;
    /** Whether scheduler is enabled by default when integration is added */
    defaultEnabled: boolean;
    /** Default interval in seconds */
    defaultIntervalSeconds: number;
  };

  /** Available integration types for this document type */
  integrationTypes: IntegrationType[];

  /** Default integration configuration when adding a new integration */
  defaultIntegration: {
    integration_type: IntegrationType;
    enabled: boolean;
    validation_method?: 'catalog' | 'quote';
    requires_confirmation_as_work_description?: boolean;
    calendar_week_delivery_weekday?: number;
    calendar_week_range_policy?: 'start' | 'end';
    calendar_week_resolved_as_work_description?: boolean;
    asap_delivery_offset_days?: number;
    asap_delivery_resolved_as_work_description?: boolean;
    output_mode?: 'refined' | 'raw';
    enrichment_enabled?: boolean;
    web_search_fallback_enabled?: boolean;
    config: Record<string, any>;
  };

  /** State ID field name for this document type */
  stateIdField: 'order_state_id' | 'document_state_id';

  /** Parsed document field name patterns */
  parsedDocumentFields: string[];

  /** Primary search field for text queries (e.g., 'customer_name' for orders) */
  searchField: string;

  /** Table columns for displaying documents in lists (e.g., BusinessDocuments) */
  tableColumns: Array<{
    /** Translation key for header (e.g., 'fieldLabels.order_number') */
    headerKey: string;
    /** Path to field in document (e.g., 'order_number' or 'parsed_document.supplier_name') */
    field: string;
    /** Optional format: 'date' for date formatting, 'truncate' for text truncation */
    format?: 'date' | 'truncate';
    /** Max width for truncated columns */
    maxWidth?: number;
  }>;
}

/**
 * Central registry of all document types and their configurations
 */
export const DOCUMENT_TYPE_REGISTRY: Record<ApiDocumentType, DocumentTypeConfig> = {
  order: {
    type: 'order',
    processType: 'sales_order',
    display: {
      label: 'Order',
      caseItems: 'lines',
      labelPlural: 'Orders',
      searchPlaceholder: 'Search by customer name...',
      infoTooltip: 'Process incoming purchase orders from customers. Validates orders against your product catalog and creates orders in your ERP system.',
    },
    route: {
      basePath: 'orders',
      detailPath: (tenantId: string, documentId: string) => `/tenant/${tenantId}/orders/${documentId}`,
    },
    scheduler: {
      key: 'order_processing',
      defaultEnabled: true,
      defaultIntervalSeconds: 60,
    },
    integrationTypes: ['business_central', 'sap_idoc_http', 'sap_idoc_email', 'email', 'rest_api'],
    defaultIntegration: {
      integration_type: 'email',
      enabled: true,
      validation_method: 'catalog',
      requires_confirmation_as_work_description: false,
      calendar_week_delivery_weekday: 0,
      calendar_week_range_policy: 'start',
      calendar_week_resolved_as_work_description: false,
      asap_delivery_offset_days: 0,
      asap_delivery_resolved_as_work_description: true,
      config: {},
    },
    stateIdField: 'order_state_id',
    parsedDocumentFields: ['parsed_order', 'parsed_document'],
    searchField: 'customer_name',
    tableColumns: [
      { headerKey: 'fieldLabels.order_number', field: 'order_number' },
    ],
  },

  order_confirmation: {
    type: 'order_confirmation',
    processType: 'order_confirmation',
    display: {
      label: 'Order Confirmation',
      caseItems: 'lines',
      labelPlural: 'Order Confirmations',
      searchPlaceholder: 'Search by order number...',
      infoTooltip: 'Process incoming order confirmations from suppliers. Matches confirmations to your purchase orders and updates delivery information.',
    },
    route: {
      basePath: 'order-confirmations',
      detailPath: (tenantId: string, documentId: string) => `/tenant/${tenantId}/order-confirmations/${documentId}`,
    },
    scheduler: {
      key: 'order_confirmation_processing',
      defaultEnabled: true,
      defaultIntervalSeconds: 300,
    },
    integrationTypes: ['email', 'rest_api'],
    defaultIntegration: {
      integration_type: 'email',
      enabled: true,
      config: {},
    },
    stateIdField: 'document_state_id',
    parsedDocumentFields: ['parsed_order_confirmation', 'parsed_document'],
    searchField: 'order_number',
    tableColumns: [
      { headerKey: 'fieldLabels.order_number', field: 'parsed_document.order_number' },
    ],
  },

  product_description: {
    type: 'product_description',
    processType: 'product_description',
    display: {
      label: 'Product Description',
      caseItems: 'articles',
      labelPlural: 'Product Descriptions',
      searchPlaceholder: 'Search by article number...',
      infoTooltip: 'Process incoming product descriptions and specifications. Extracts product data from emails and documents for catalog updates.',
    },
    route: {
      basePath: 'product-descriptions',
      detailPath: (tenantId: string, documentId: string) => `/tenant/${tenantId}/product-descriptions/${documentId}`,
    },
    scheduler: {
      key: 'product_description_processing',
      defaultEnabled: true,
      defaultIntervalSeconds: 300,
    },
    integrationTypes: ['email'],
    defaultIntegration: {
      integration_type: 'email',
      enabled: true,
      output_mode: 'refined',
      enrichment_enabled: true,
      web_search_fallback_enabled: true,
      config: {},
    },
    stateIdField: 'document_state_id',
    parsedDocumentFields: ['parsed_product_description', 'parsed_document'],
    searchField: 'articlenumber',
    tableColumns: [
      { headerKey: 'fieldLabels.articlenumber', field: 'parsed_document.articlenumber' },
      { headerKey: 'fieldLabels.title', field: 'parsed_document.title', format: 'truncate', maxWidth: 200 },
    ],
  },

  incoming_quote: {
    type: 'incoming_quote',
    processType: 'incoming_quote',
    display: {
      label: 'Incoming Quote',
      caseItems: 'lines',
      labelPlural: 'Incoming Quotes',
      searchPlaceholder: 'Search by supplier name...',
      infoTooltip: 'Process incoming quotes from suppliers. Extracts pricing and availability information for comparison and decision-making.',
    },
    route: {
      basePath: 'incoming-quotes',
      detailPath: (tenantId: string, documentId: string) => `/tenant/${tenantId}/incoming-quotes/${documentId}`,
    },
    scheduler: {
      key: 'incoming_quote_processing',
      defaultEnabled: true,
      defaultIntervalSeconds: 300,
    },
    integrationTypes: ['email'],
    defaultIntegration: {
      integration_type: 'email',
      enabled: true,
      config: {},
    },
    stateIdField: 'document_state_id',
    parsedDocumentFields: ['parsed_incoming_quote', 'parsed_document'],
    searchField: 'supplier_name',
    tableColumns: [
      { headerKey: 'fieldLabels.supplier_name', field: 'parsed_document.supplier_name' },
      { headerKey: 'fieldLabels.quote_number', field: 'parsed_document.quote_number' },
    ],
  },

  backorder_report: {
    type: 'backorder_report',
    processType: 'backorder_report',
    display: {
      label: 'Backorder Report',
      caseItems: 'lines',
      labelPlural: 'Backorder Reports',
      searchPlaceholder: 'Search by supplier name...',
      infoTooltip: 'Process backorder reports from suppliers. Tracks delayed deliveries and updates expected delivery dates in your system.',
    },
    route: {
      basePath: 'backorder-reports',
      detailPath: (tenantId: string, documentId: string) => `/tenant/${tenantId}/backorder-reports/${documentId}`,
    },
    scheduler: {
      key: 'backorder_report_processing',
      defaultEnabled: true,
      defaultIntervalSeconds: 300,
    },
    integrationTypes: ['email'],
    defaultIntegration: {
      integration_type: 'email',
      enabled: true,
      config: {},
    },
    stateIdField: 'document_state_id',
    parsedDocumentFields: ['parsed_backorder_report', 'parsed_document'],
    searchField: 'supplier_name',
    tableColumns: [
      { headerKey: 'fieldLabels.supplier_name', field: 'parsed_document.supplier_name' },
      { headerKey: 'fieldLabels.report_date', field: 'parsed_document.report_date', format: 'date' },
    ],
  },
};

/**
 * List of all document types in display order
 */
export const DOCUMENT_TYPES: ApiDocumentType[] = [
  'order',
  'order_confirmation',
  'product_description',
  'incoming_quote',
  'backorder_report',
];

/**
 * Get configuration for a specific document type
 */
export function getDocumentTypeConfig(type: ApiDocumentType): DocumentTypeConfig {
  const config = DOCUMENT_TYPE_REGISTRY[type];
  if (!config) {
    console.warn(`[DOCUMENT_TYPE_REGISTRY] Unknown document type: ${type}, defaulting to order`);
    return DOCUMENT_TYPE_REGISTRY.order;
  }
  return config;
}

/**
 * Check if a string is a valid API document type
 */
export function isValidDocumentType(type: string): type is ApiDocumentType {
  return type in DOCUMENT_TYPE_REGISTRY;
}

/** The backend process type id for an API document type. */
export function processTypeOf(type: ApiDocumentType): string {
  return DOCUMENT_TYPE_REGISTRY[type].processType;
}
